// src/config/lifeDomains.js
export const LIFE_DOMAINS = [
  {
    id: "family-decisions",
    label: "Family Decisions",
    eyebrow: "Integration • Family",
    title: "Aligning money with the relationships that matter most.",
    summary:
      "Blending finances with family brings emotions, history, and tradeoffs. We help you make thoughtful decisions that protect both relationships and stability.",
    whatWeSee: [
      "Tension between helping adult children and staying on track for retirement.",
      "Unclear expectations around inheritances and gifts.",
      "Disagreements between spouses about priorities and risk.",
    ],
    howWeHelp: [
      "Create a shared, values-based plan you both can stand behind.",
      "Model different gifting and support scenarios before you commit.",
      "Facilitate calm, structured conversations across generations.",
    ],
    proof:
      "We use a documented framework so family decisions are revisited with facts, not friction.",
    sageFocus: "SAGE 1 (clarity) + SAGE 2 (behavior).",
  },
  {
    id: "housing-real-estate",
    label: "Housing & Real Estate",
    eyebrow: "Integration • Housing & Real Estate",
    title: "Turning homes and properties into part of a coherent plan.",
    summary:
      "Moves, second homes, and investment properties impact cash flow, taxes, and risk. We connect the dots before you sign anything.",
    whatWeSee: [
      "Buying or keeping a second home without fully mapping the cash requirements.",
      "Unclear plan for when to downsize or relocate.",
      "Real-estate decisions made in isolation from portfolios and estate planning.",
    ],
    howWeHelp: [
      "Run side-by-side scenarios on buying, selling, or holding.",
      "Coordinate with lenders, CPAs, and attorneys so decisions fit the full picture.",
      "Build a funding plan for major repairs, upgrades, or vacancies.",
    ],
    proof:
      "Real-estate choices are plugged into the same 5-step Harborline Framework as your investments.",
    sageFocus: "SAGE 3 (framework) + SAGE 4 (scenario planning).",
  },
  {
    id: "education-planning",
    label: "Education Planning",
    eyebrow: "Integration • Education",
    title: "Funding education without derailing your own future.",
    summary:
      "We help you decide how much to fund, for whom, and from which accounts—without losing sight of your own retirement.",
    whatWeSee: [
      "Over-funding 529s before retirement is on track.",
      "Unclear expectations between parents and kids around what will be covered.",
      "Gifts and support spread across many accounts with no cohesive strategy.",
    ],
    howWeHelp: [
      "Clarify your philosophy around education support and boundaries.",
      "Design a tax-aware funding plan across 529s, brokerage, and cash.",
      "Translate complex rules into a simple, living plan you can explain to your family.",
    ],
    proof:
      "Education is treated as one chapter in a long-term plan, not a disconnected project.",
    sageFocus: "SAGE 3 (planning) + SAGE 1 (long-term orientation).",
  },
  {
    id: "business-cash-flows",
    label: "Business Cash Flows",
    eyebrow: "Integration • Business Owners",
    title: "Connecting business cash flows to your personal balance sheet.",
    summary:
      "We help owners turn irregular cash flows, distributions, and retained earnings into a stable, long-term wealth plan.",
    whatWeSee: [
      "Most wealth tied up in the operating company with no clear diversification path.",
      "Distributions driven by tax deadlines instead of a long-term plan.",
      "No clear strategy for how much to reinvest vs. take off the table.",
    ],
    howWeHelp: [
      "Map your business P&L and distributions into a personal cash-flow plan.",
      "Create rules for when to pull funds out—and where they go.",
      "Build pre-exit and post-exit playbooks so decisions feel deliberate, not reactive.",
    ],
    proof:
      "Your company and personal finances live in a single, integrated framework.",
    sageFocus: "SAGE 3 (system) + SAGE 4 (what-if on income).",
  },
  {
    id: "aging-parents",
    label: "Aging Parents",
    eyebrow: "Integration • Aging Parents",
    title: "Supporting parents with care, clarity, and financial stability.",
    summary:
      "We help you plan for care, housing, and support in a way that honors your parents and protects your own future.",
    whatWeSee: [
      "Unclear expectations among siblings about who will help and how.",
      "Surprise care costs that strain your own savings.",
      "Paperwork and accounts no one has fully mapped.",
    ],
    howWeHelp: [
      "Estimate care scenarios and build a funding plan around them.",
      "Clarify roles and expectations with family members.",
      "Organize accounts, documents, and decision-makers so you’re not scrambling.",
    ],
    proof:
      "We turn an emotionally heavy topic into a series of calm, repeatable steps.",
    sageFocus: "SAGE 2 (behavior) + SAGE 4 (care scenarios).",
  },
  {
    id: "career-shifts",
    label: "Career Shifts",
    eyebrow: "Integration • Career",
    title: "Making big career decisions from a position of strength.",
    summary:
      "We help you understand the financial runway around promotions, sabbaticals, relocations, or full career changes.",
    whatWeSee: [
      "People delaying meaningful career moves because the numbers are unclear.",
      "Equity and benefits left on the table during transitions.",
      "No clear plan for how long savings will last between roles.",
    ],
    howWeHelp: [
      "Model best-case, base-case, and conservative paths around your move.",
      "Optimize around RSUs, bonuses, and benefits during the transition.",
      'Give you a simple "yes/no" framework so the decision feels grounded.',
    ],
    proof:
      "Career changes are framed as planned transitions, not leaps into the unknown.",
    sageFocus: "SAGE 4 (transition playbook).",
  },
  {
    id: "liquidity-events",
    label: "Liquidity Events",
    eyebrow: "Integration • Liquidity",
    title: "Turning one-time liquidity into decades of calm, durable wealth.",
    summary:
      "We help you slow decisions down, structure proceeds, and avoid the most common post-liquidity regrets.",
    whatWeSee: [
      "Spending and gifting decisions made before a plan is in place.",
      "Concentrated stock held without a clear diversification roadmap.",
      "Tax surprises that could have been planned for years earlier.",
    ],
    howWeHelp: [
      "Create a pre- and post-event decision map before money moves.",
      "Design a portfolio that balances safety, growth, and flexibility.",
      "Coordinate your tax, legal, and estate team around a single plan.",
    ],
    proof:
      "Every major decision passes through a simple 5-step Harborline Framework, documented in writing.",
    sageFocus: "SAGE 3 (framework) + SAGE 4 (liquidity scenarios).",
  },
  {
    id: "unexpected-changes",
    label: "Unexpected Changes",
    eyebrow: "Integration • Resilience",
    title: "Building a plan that can bend without breaking.",
    summary:
      "Job loss, illness, divorce, or market shocks—your plan should already know what to do when they happen.",
    whatWeSee: [
      "Emergency decisions made in the moment instead of from a prepared playbook.",
      "No clear order of what to cut, sell, or pause if income changes.",
      "Insurance and protections that exist on paper but not in a real plan.",
    ],
    howWeHelp: [
      "Define trigger points and pre-decided actions for shocks.",
      "Build cash buffers and flexible portfolios around your real life.",
      "Coordinate legal and insurance protections into one resilience plan.",
    ],
    proof:
      "Resilience is baked into your plan upfront, not added as an afterthought.",
    sageFocus: "SAGE 4 (resilience & contingency).",
  },
];
